import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Mic, Search, X, ArrowRight, CornerDownLeft, BookOpen, Layers } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ARTICLES, PROJECTS } from "../../data/mock";

export default function VoiceSearch({ open, onClose }) {
  const [q, setQ] = useState("");
  const [listening, setListening] = useState(false);
  const [speechError, setSpeechError] = useState(null);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQ("");
      setListening(false);
      setSpeechError(null);
    }
  }, [open]);

  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!open) return;
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  // Real search filtering across projects and articles
  const matches = q.trim().length > 0
    ? [
        ...PROJECTS.filter((p) => {
          const query = q.toLowerCase();
          const inName = p.name.toLowerCase().includes(query);
          const inDesc = p.desc.toLowerCase().includes(query);
          const inStack = p.stack?.some((t) => t.toLowerCase().includes(query));
          return inName || inDesc || inStack;
        }).map((p) => ({
          kind: "Work",
          title: p.name,
          sub: p.type,
          href: `/works/${p.slug || p.name.toLowerCase().replace(/\s+/g, "-")}`,
          icon: Layers,
        })),
        ...ARTICLES.filter((a) => {
          const query = q.toLowerCase();
          const inTitle = a.title.toLowerCase().includes(query);
          const inExcerpt = a.excerpt.toLowerCase().includes(query);
          const inTags = a.tags?.some((t) => t.toLowerCase().includes(query));
          return inTitle || inExcerpt || inTags;
        }).map((a) => ({
          kind: "Article",
          title: a.title,
          sub: a.category,
          href: `/articles/${a.slug}`,
          icon: BookOpen,
        })),
      ]
    : [];

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && matches.length > 0) {
      onClose();
      navigate(matches[0].href);
    }
  };

  // Real Web Speech API integration (safely checks browser support)
  const toggleSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setSpeechError("Voice input is not supported in this browser. Please type to search.");
      setTimeout(() => setSpeechError(null), 3500);
      return;
    }

    if (listening) {
      setListening(false);
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.lang = "en-US";
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        setListening(true);
        setSpeechError(null);
      };

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setQ(transcript);
        setListening(false);
      };

      recognition.onerror = (event) => {
        setListening(false);
        if (event.error !== "no-speech") {
          setSpeechError(`Voice error: ${event.error}. Please type directly.`);
          setTimeout(() => setSpeechError(null), 3000);
        }
      };

      recognition.onend = () => {
        setListening(false);
      };

      recognition.start();
    } catch (err) {
      setListening(false);
      setSpeechError("Microphone permission or recognition failed.");
      setTimeout(() => setSpeechError(null), 3000);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-start justify-center pt-[10vh] sm:pt-[14vh] px-4"
          onClick={onClose}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Search portfolio"
            initial={{ y: -16, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: -10, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl rounded-2xl border border-border/80 bg-[#0d0d12]/95 backdrop-blur-xl shadow-2xl overflow-hidden"
          >
            {/* Input Bar */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-border/60">
              <Search className="h-4 w-4 text-muted-foreground shrink-0" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search systems, field notes, or stack..."
                aria-label="Search query"
                className="flex-1 bg-transparent outline-none text-[15px] placeholder:text-muted-foreground/70 text-foreground"
              />
              <button
                onClick={toggleSpeechRecognition}
                aria-label={listening ? "Stop voice listening" : "Start voice search"}
                title={listening ? "Stop listening" : "Search with voice"}
                className={`h-9 w-9 inline-flex items-center justify-center rounded-full border border-border/60 transition-colors ${
                  listening
                    ? "bg-rose-500/20 text-rose-300 border-rose-400/50 animate-pulse"
                    : "hover:bg-secondary/60 text-muted-foreground hover:text-foreground"
                }`}
              >
                <Mic className="h-4 w-4" />
              </button>
              <button
                onClick={onClose}
                aria-label="Close search"
                className="h-9 w-9 inline-flex items-center justify-center rounded-full hover:bg-secondary/60 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Listening Indicator */}
            {listening && (
              <div className="px-5 py-3 border-b border-border/40 flex items-center gap-2 font-mono text-[11px] text-rose-300 bg-rose-500/10">
                <span className="h-2 w-2 rounded-full bg-rose-400 animate-ping" />
                Listening to microphone input... Speak clearly.
              </div>
            )}

            {/* Speech error notification */}
            {speechError && (
              <div className="px-5 py-2.5 border-b border-border/40 font-mono text-[11px] text-amber-300 bg-amber-500/10">
                {speechError}
              </div>
            )}

            {/* Results List */}
            <ul className="max-h-[55vh] overflow-y-auto divide-y divide-border/40">
              {matches.length === 0 && q && (
                <li className="px-5 py-10 text-center text-muted-foreground font-mono text-[13px]">
                  No matching systems or essays found for &ldquo;{q}&rdquo;
                </li>
              )}

              {matches.map((m, i) => {
                const Icon = m.icon;
                return (
                  <li key={i}>
                    <button
                      onClick={() => {
                        onClose();
                        navigate(m.href);
                      }}
                      className="w-full text-left px-5 py-3.5 flex items-center justify-between hover:bg-secondary/40 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-4 w-4 text-primary shrink-0" />
                        <div>
                          <span className="text-[14.5px] font-medium text-foreground group-hover:text-primary transition-colors block">
                            {m.title}
                          </span>
                          <span className="font-mono text-[10px] tracking-wider uppercase text-muted-foreground">
                            {m.kind} · {m.sub}
                          </span>
                        </div>
                      </div>
                      <span className="h-7 w-7 rounded-full border border-border/60 group-hover:border-primary group-hover:bg-primary/10 flex items-center justify-center text-muted-foreground group-hover:text-primary transition-colors">
                        <ArrowRight className="h-3 w-3" />
                      </span>
                    </button>
                  </li>
                );
              })}

              {!q && (
                <li className="px-5 py-5 text-[12px] font-mono text-muted-foreground space-y-2">
                  <p className="uppercase tracking-wider text-[10px] text-primary">Suggested Searches</p>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {["Vistara", "MaveMentor", "Design Systems", "RAG", "Tokens"].map((s) => (
                      <button
                        key={s}
                        onClick={() => setQ(s)}
                        className="px-2.5 py-1 rounded-md border border-border/60 bg-secondary/30 text-foreground/80 hover:text-primary hover:border-primary/50 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </li>
              )}
            </ul>

            {/* Modal Footer Key hint */}
            <div className="px-5 py-2.5 border-t border-border/60 bg-card/40 flex items-center justify-between font-mono text-[10.5px] text-muted-foreground">
              <span>Press <kbd className="px-1.5 py-0.5 rounded border border-border/70 bg-secondary/40">ESC</kbd> to exit</span>
              <span className="flex items-center gap-1">
                Press <kbd className="px-1.5 py-0.5 rounded border border-border/70 bg-secondary/40">↵ Enter</kbd> to select
              </span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
